import { Link, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Download, ExternalLink, ImageIcon, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { API_BASE, api, formatApiError } from "@/lib/api";
import { LEAD_STATUS, callbackLabel, causeLabel } from "@/lib/leadLabels";
import { cn } from "@/lib/utils";
import { fmtDate, StatusBadge, TypeBadge } from "@/pages/admin/badges";

const Row = ({ label, value, testId }) =>
  value ? (
    <div className="grid grid-cols-[140px_1fr] gap-3 border-b border-slate-100 py-2.5 text-sm last:border-0">
      <dt className="font-semibold text-slate-500">{label}</dt>
      <dd className="whitespace-pre-wrap break-words text-brand-anthracite" data-testid={testId}>{value}</dd>
    </div>
  ) : null;

const Card = ({ title, children }) => (
  <section className="rounded-2xl bg-white p-6 shadow-soft ring-1 ring-slate-100">
    <h2 className="mb-3 text-sm font-bold uppercase tracking-wider text-brand-blue">{title}</h2>
    {children}
  </section>
);

const Photos = ({ lead }) => {
  if (lead.photos_expired) return <p className="text-sm text-slate-500">Fotos nach 180 Tagen gesperrt.</p>;
  if (!lead.photos.length) return <p className="text-sm text-slate-500">Keine Fotos hochgeladen.</p>;
  return (
    <ul className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4" data-testid="admin-lead-photos">
      {lead.photos.map((p, i) => {
        const url = `${API_BASE}/admin/leads/${lead.id}/photos/${p.id}`;
        const previewable = !p.content_type.includes("hei");
        return (
          <li key={p.id} className="overflow-hidden rounded-xl ring-1 ring-slate-200">
            <div className="grid aspect-[4/3] place-items-center bg-brand-mist">
              {previewable ? <img src={url} alt={`Schadensfoto ${i + 1}`} loading="lazy" className="h-full w-full object-cover" data-testid={`admin-photo-${i}`} />
                : <span className="text-center text-xs text-slate-500"><ImageIcon className="mx-auto mb-1 h-6 w-6" />HEIC – bitte herunterladen</span>}
            </div>
            <div className="flex items-center justify-between gap-2 px-3 py-2 text-xs">
              <span className="truncate" title={p.original_filename}>{p.original_filename}</span>
              <span className="flex shrink-0 gap-2">
                <a href={url} target="_blank" rel="noopener noreferrer" aria-label="Ansehen" className="text-brand-blue" data-testid={`admin-photo-view-${i}`}><ExternalLink className="h-4 w-4" /></a>
                <a href={`${url}?download=true`} aria-label="Herunterladen" className="text-brand-blue" data-testid={`admin-photo-download-${i}`}><Download className="h-4 w-4" /></a>
              </span>
            </div>
          </li>
        );
      })}
    </ul>
  );
};

export default function AdminLeadDetail() {
  const { id } = useParams();
  const qc = useQueryClient();
  const { data: lead, isLoading, isError } = useQuery({ queryKey: ["lead", id], queryFn: () => api.get(`/admin/leads/${id}`).then((r) => r.data) });
  const mutation = useMutation({
    mutationFn: (status) => api.patch(`/admin/leads/${id}`, { status }).then((r) => r.data),
    onSuccess: (data) => {
      qc.setQueryData(["lead", id], data);
      qc.invalidateQueries({ queryKey: ["leads"] });
      toast.success(`Status: ${LEAD_STATUS[data.status]}`);
    },
    onError: (err) => toast.error(formatApiError(err)),
  });

  if (isLoading) return <div className="grid place-items-center py-24"><Loader2 className="h-6 w-6 animate-spin text-brand-blue" /></div>;
  if (isError) return <p className="py-16 text-center text-red-600" data-testid="admin-lead-error">Anfrage nicht gefunden.</p>;

  return (
    <div className="space-y-6" data-testid="admin-lead-detail">
      <Link to="/admin" className="inline-flex items-center gap-1.5 text-sm font-bold text-brand-blue hover:underline" data-testid="admin-back-link">
        <ArrowLeft className="h-4 w-4" /> Alle Anfragen
      </Link>
      <div className="flex flex-wrap items-start justify-between gap-4 rounded-2xl bg-white p-6 shadow-soft ring-1 ring-slate-100">
        <div>
          <div className="flex items-center gap-2"><TypeBadge type={lead.type} /><StatusBadge status={lead.status} testId="admin-detail-status" /></div>
          <h1 className="mt-3 text-2xl font-bold text-brand-anthracite" data-testid="admin-detail-name">{lead.name}</h1>
          <p className="text-sm text-slate-500"><span data-testid="admin-detail-ref">{lead.ref}</span> · eingegangen am {fmtDate(lead.created_at)}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          {Object.entries(LEAD_STATUS).map(([key, label]) => (
            <button key={key} type="button" disabled={mutation.isPending || lead.status === key} onClick={() => mutation.mutate(key)} data-testid={`admin-set-status-${key}`}
              className={cn("rounded-full px-4 py-2 text-sm font-bold transition-colors disabled:cursor-default",
                lead.status === key ? "bg-brand-anthracite text-white" : "bg-brand-mist text-brand-anthracite hover:bg-brand-sky-light")}>
              {label}
            </button>
          ))}
        </div>
      </div>
      <div className="grid gap-6 lg:grid-cols-2">
        <Card title="Kontakt">
          <dl>
            <Row label="Telefon" value={lead.phone && <a href={`tel:${lead.phone}`} className="text-brand-blue">{lead.phone}</a>} testId="admin-detail-phone" />
            <Row label="E-Mail" value={lead.email && <a href={`mailto:${lead.email}`} className="text-brand-blue">{lead.email}</a>} testId="admin-detail-email" />
            <Row label="Wunschzeit" value={lead.callback_time && callbackLabel(lead.callback_time)} />
            <Row label="Rückruftermin" value={lead.callback_at && fmtDate(lead.callback_at)} />
          </dl>
        </Card>
        <Card title="Anfrage">
          <dl>
            <Row label="Anliegen" value={lead.concern} />
            <Row label="Leistung" value={lead.service} />
            <Row label="Nachricht" value={lead.message} testId="admin-detail-message" />
            <Row label="Schadenursache" value={lead.cause && causeLabel(lead.cause)} testId="admin-detail-cause" />
            <Row label="PLZ" value={lead.plz} testId="admin-detail-plz" />
            <Row label="Kennzeichen" value={lead.plate} />
            <Row label="Unfalldatum" value={lead.accident_date} />
            <Row label="Fahrbereit" value={lead.drivable} />
            <Row label="Gegn. Versicherung" value={lead.opponent_insurance} />
          </dl>
        </Card>
      </div>
      {lead.type === "damage" && <Card title={`Fotos (${lead.photos.length})`}><Photos lead={lead} /></Card>}
      <Card title="Herkunft & Tracking">
        <dl>
          <Row label="Herkunftsseite" value={lead.source_page} testId="admin-detail-source" />
          <Row label="Einstiegsseite" value={lead.landing_page} />
          <Row label="Referrer" value={lead.referrer} />
          {["utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content", "gclid"].map((k) => <Row key={k} label={k} value={lead[k]} testId={`admin-detail-${k.replace("_", "-")}`} />)}
          <Row label="Einwilligung" value={fmtDate(lead.consent_at)} />
        </dl>
      </Card>
    </div>
  );
}
